import { ApiClient } from "../../api/ApiClient";
import { ApiError, apiErrorCodes } from "../../api/ApiResponse";
import {
  currencyDefaultsSelector,
  supportedTickerSymbolMapSelector,
  countryCodeToCurrencyCodeMap,
  expandTickerKeys,
  exchangeCodeToNameMap
} from "../reducers/TickerReducer";
import { userCountryCodeSelector } from "../reducers/AuthReducer";
import {
  getCustodianHistoryFormattedDateString,
  getUuid,
  getValueOfPropFromStringifiedJSON
} from "../../utilities/Number";
import {
  getExchangeRateDetails,
  updateDashboardAction,
  UPDATE_TICKER_DATA,
  tickerTypes,
  getSymbolForTickerUsingShortName,
  shortFormatNumberWithCurrency,
  ADD_TICKER_INFO,
  ADD_TICKER_INFO_FOR_RATE_HISTORY,
  isCryptoCurrency,
  UPDATE_SUPPORTED_INDEXES
} from "./Common";
import { portfolioQuantityForTicker } from "../reducers/PortfolioReducer";
import {
  currentPortfolioSelector,
  SIMILAR_FORMAT_CURRENCIES,
  HARDCODED_EXCHANGE_RATES,
  cleanUpSeparatorFromNumberString,
  UNKNOWN_TICKER_SHORT_NAME,
  getCharacterForDecimal,
  getCharacterForNumberSeparator,
  getDateStringForExchangeRateDate,
  getExchangeRate,
  getTickerUsingId,
  getTickerUsingShortName,
  parseNumberStringToFloat,
  formatNumberWithKuberaNumberFormatSettingsSelector,
  currencyPreviousDayExchangeRateSelector,
  currencyExchangeRateSelector,
  PVST_VALUE_TICKER_ID
} from "../reducers/Common";
import { toUserTimezoneDate } from "../../utilities/Timezone";
// spanish speaking countries use long scale: https://en.wikipedia.org/wiki/Long_and_short_scales

export const unsupportedTickerSubTypesArray = [];

export const updateTickerDataAction = tickerData => ({
  type: UPDATE_TICKER_DATA,
  tickerData
});

export const addTickerInfoAction = (tickerInfo, exchangeRate, date) => ({
  type: ADD_TICKER_INFO,
  tickerInfo,
  exchangeRate,
  date
});

export const addTickerInfoForRateHistoryAction = (fromCurrency, toCurrency, exchangeRate, date) => ({
  type: ADD_TICKER_INFO_FOR_RATE_HISTORY,
  fromCurrency,
  toCurrency,
  exchangeRate,
  date
});

export const updateSupportedIndexesAction = indexes => ({
  type: UPDATE_SUPPORTED_INDEXES,
  indexes
});

export function convertPVSTRateToValueExchangeRate(
  rate,
  guessDateInCustodianHistoryUIFormatStr,
  isExhangeRateForPVSTConversion = true,
  handleFallbackFromEntry = null,
  logValues = false
) {
  return (dispatch, getState) => {
    const state = getState();
    rate = typeof rate === "string" ? JSON.parse(rate) : rate;
    const currentPortfolio = currentPortfolioSelector(state);

    const dateStringForToday = getCustodianHistoryFormattedDateString(toUserTimezoneDate());
    const applicableDate =
      guessDateInCustodianHistoryUIFormatStr && dateStringForToday !== guessDateInCustodianHistoryUIFormatStr
        ? new Date(guessDateInCustodianHistoryUIFormatStr)
        : undefined;

    if (!rate === true) {
      return getExchangeRateDetails(getTickerUsingShortName(state, currentPortfolio.currency).id, 0, applicableDate);
    }
    let exchangeRate = getExchangeRate(
      state,
      getTickerUsingId(state, rate.t).shortName,
      currentPortfolio.currency,
      undefined,
      applicableDate,
      logValues
    );
    if (exchangeRate === 0 && applicableDate && handleFallbackFromEntry?.valueExchangeRate) {
      exchangeRate = Number(getValueOfPropFromStringifiedJSON(handleFallbackFromEntry.valueExchangeRate, "rate"));
      dispatch(
        addTickerInfoForRateHistoryAction(
          getTickerUsingId(state, rate.t).shortName,
          currentPortfolio.currency,
          exchangeRate,
          applicableDate
        )
      );
    }

    if (logValues) {
      console.log("convertPVSTRateToValueExchangeRate", applicableDate, rate, currentPortfolio.currency, exchangeRate);
    }

    return getExchangeRateDetails(
      getTickerUsingShortName(state, currentPortfolio.currency).id,
      isExhangeRateForPVSTConversion ? exchangeRate * rate.p : exchangeRate,
      applicableDate
    );
  };
}

export const isValidFromToCurrencyRate = (
  state,
  fromCurrency,
  toCurrency,
  usePreviousDayRates = false,
  date = null,
  tickerId
) => {
  if (!fromCurrency === true || !toCurrency === true) {
    return false;
  }

  if (fromCurrency === toCurrency) {
    return true;
  }

  const key = fromCurrency + "-" + toCurrency;
  const hardcodedRate = HARDCODED_EXCHANGE_RATES.get(key);
  if (hardcodedRate) {
    return true;
  }

  const previousDayRates = currencyPreviousDayExchangeRateSelector(state);
  const currentRates = currencyExchangeRateSelector(state);
  const exchangeRates = usePreviousDayRates && !previousDayRates === false ? previousDayRates : currentRates;
  var fromCurrencyExchangeRate = exchangeRates[fromCurrency];
  var toCurrencyExchangeRate = exchangeRates[toCurrency];
  if (!date === false) {
    const dateString = getDateStringForExchangeRateDate(date);
    fromCurrencyExchangeRate = exchangeRates[fromCurrency === "USD" ? fromCurrency : `${fromCurrency}-${dateString}`];
    toCurrencyExchangeRate = exchangeRates[toCurrency === "USD" ? toCurrency : `${toCurrency}-${dateString}`];
  }
  if (!fromCurrencyExchangeRate === true || !toCurrencyExchangeRate === true) {
    return tickerId === PVST_VALUE_TICKER_ID ? true : false;
  }

  return true;
};

const extractValueAndCurrencyInputFromInput = (state, input) => {
  input = cleanUpSeparatorFromNumberString(state, input).trim();

  // Find symbol for negative number inputs and remove it so
  // that it doesnt interfere with tickers having - in them
  var negativeCharIndex = -1;
  for (var i = 0; i < input.length; i++) {
    if (input[i] === "-") {
      if (i === 0) {
        negativeCharIndex = i;
        break;
      }

      if (i + 1 < input.length && isNaN(input.slice(i, input.length)) === false) {
        negativeCharIndex = i;
        break;
      }
    }
  }
  if (negativeCharIndex >= 0) {
    input = input.slice(0, negativeCharIndex) + input.slice(negativeCharIndex + 1);
  }

  // Try to find the value from the end for inputs like
  // USD 200 or 4BTC 10 which are more likely
  var possibleValues = getCharacterForDecimal(state) === "," ? input.match(/[0-9,]*$/gi) : input.match(/[0-9.]*$/gi);
  var isValueAtEnd = true;
  if (!possibleValues === true || possibleValues.length === 0 || possibleValues[0].trim().length === 0) {
    isValueAtEnd = false;
    possibleValues = getCharacterForDecimal(state) === "," ? input.match(/[0-9,]*/gi) : input.match(/[0-9.]*/gi);
  }

  // If no value find try to find the value from the
  // starting like 100 INR or 100 BTC
  var valueString = "";
  if (!possibleValues === false && possibleValues.length > 0 && possibleValues[0].trim().length > 0) {
    valueString = possibleValues[0];
  }

  var currencyInput = input.replace(valueString, "").trim();
  if (isValueAtEnd === true) {
    currencyInput = input.substring(0, input.lastIndexOf(valueString));
  }
  return { valueInput: valueString, currencyInput: currencyInput, isNegative: negativeCharIndex >= 0 };
};

export const getAmountAndTickerFromInputSelector = (state, input, fallbackCurrency) => {
  const parsedInput = extractValueAndCurrencyInputFromInput(state, input);
  const valueString = parsedInput.valueInput;
  const isNegative = parsedInput.isNegative;
  const currencyInput = parsedInput.currencyInput;
  const currency = mapCurrencySymbolOrCodeToCurrencySelector(state, currencyInput, fallbackCurrency);

  return {
    ticker:
      currency === null ? getTickerUsingShortName(state, fallbackCurrency) : getTickerUsingShortName(state, currency),
    isTickerInputValid: currencyInput.trim().length === 0 || currency !== null,
    isTickerInputEmpty: !currencyInput === true,
    tickerInput: !currencyInput === true ? currencyInput : currencyInput.trim(),
    value: parseNumberStringToFloat(state, isNegative === true ? "-" + valueString : valueString)
  };
};

export const mapCurrencySymbolOrCodeToCurrencySelector = (state, input, defaultCurrency) => {
  if (!input === true) {
    return null;
  }

  input = input.trim();

  if (input === "") {
    return null;
  }
  if (input === defaultCurrency) {
    return defaultCurrency;
  }

  const currencyDefaults = currencyDefaultsSelector(state);
  const userCountryCode = userCountryCodeSelector(state);
  const defaultCurrencyDetails = getTickerUsingShortName(state, defaultCurrency);

  // If the user enters a symbol as input give priority to
  // the currency of the present country he is in over what is provided in defaults
  // to determine the applicable currency
  // Only exception being when the symbol for the default currency and
  // the symbol for the current country are same.
  if (userCountryCode) {
    const userCountryCurrency = countryCodeToCurrencyCodeMap[userCountryCode];

    if (userCountryCurrency) {
      const userCountryCurrencyDetails = getTickerUsingShortName(state, userCountryCurrency);
      const symbolForDefaultCurrency = getSymbolForTickerUsingShortName(state, defaultCurrency);

      if (userCountryCurrencyDetails && userCountryCurrencyDetails.symbol.includes(input)) {
        if (input === symbolForDefaultCurrency) {
          return defaultCurrency;
        } else {
          return userCountryCurrency;
        }
      }
    }
  }

  if (defaultCurrencyDetails) {
    if (defaultCurrencyDetails.symbol.includes(input)) {
      return defaultCurrency;
    }
  }

  if (currencyDefaults[input]) {
    return currencyDefaults[input];
  }

  const tickerGuessUsingShortName = getTickerUsingShortName(state, input.toUpperCase());
  if (tickerGuessUsingShortName.shortName !== UNKNOWN_TICKER_SHORT_NAME) {
    return tickerGuessUsingShortName.shortName;
  }

  const tickerGuessUsingSymbol = getTickerUsingSymbolSelector(state, input);
  if (tickerGuessUsingSymbol.shortName !== UNKNOWN_TICKER_SHORT_NAME) {
    return tickerGuessUsingSymbol.shortName;
  }
  return null;
};

export const getTickerUsingSymbolSelector = (state, symbol) => {
  const supportedTickers = supportedTickerSymbolMapSelector(state);
  const result = supportedTickers[symbol];
  return result || getTickerUsingShortName(state, UNKNOWN_TICKER_SHORT_NAME);
};

export const getExchangeRateFromDetailsString = (state, details, fromCurrency, forCurrency) => {
  let exchangeRateDetails = null;
  try {
    exchangeRateDetails = JSON.parse(details);
  } catch (e) {}
  if (!exchangeRateDetails === true) {
    return getExchangeRate(state, fromCurrency, forCurrency);
  }

  if (fromCurrency === forCurrency) {
    return 1;
  }

  const forCurrencyTicker = getTickerUsingShortName(state, forCurrency);
  if (forCurrencyTicker.id === exchangeRateDetails.tickerId) {
    return exchangeRateDetails.rate;
  } else {
    const detailsTargetTicker = getTickerUsingId(state, exchangeRateDetails.tickerId);

    // This is to fix a bug where for tickers post searching we were using the conversion
    // rate to USD instead of mapping that to the tickers currency rate
    if (detailsTargetTicker.shortName === fromCurrency && exchangeRateDetails.rate !== 1) {
      return (1 / exchangeRateDetails.rate) * getExchangeRate(state, "USD", forCurrency);
    }

    return exchangeRateDetails.rate * getExchangeRate(state, detailsTargetTicker.shortName, forCurrency);
  }
};

export const formatNumberAsCurrency = (state, number, currencyCode) => {
  if (!number === true) {
    return number;
  }

  const maxFraction = isCryptoCurrency(state, currencyCode) ? 8 : 2;
  if (SIMILAR_FORMAT_CURRENCIES.get("INR").includes(currencyCode) && getCharacterForNumberSeparator(state) === ",") {
    return parseFloat(number).toLocaleString("en-IN", {
      maximumFractionDigits: maxFraction
    });
  } else {
    return formatNumberWithKuberaNumberFormatSettingsSelector(state, parseFloat(number), {
      maximumFractionDigits: maxFraction
    });
  }
};

export const refreshTickerData = (onSuccess = null, onError = null) => {
  return dispatch => {
    ApiClient.getTickerData(getUuid())
      .then(apiData => {
        dispatch(updateTickerDataAction(apiData.payload));
        dispatch(updateDashboardAction(null));

        if (!onSuccess === false) {
          onSuccess(apiData);
        }
      })
      .catch(apiError => {
        if (!onError === false) {
          onError(apiError);
        }
      });
  };
};

export const getTickersForIds = (tickerIds, onSuccess = () => null, onError = () => null) => {
  return dispatch => {
    return new Promise((resolve, reject) => {
      if (!tickerIds === true || tickerIds.length === 0) {
        if (!onError === true) {
          onError();
        }
      }

      var idString = tickerIds.join(",");

      ApiClient.getTickersForIds(getUuid(), idString)
        .then(apiData => {
          for (var tickerInfo of apiData.payload) {
            tickerInfo.info = expandTickerKeys(tickerInfo.info);
            dispatch(addTickerInfoAction(tickerInfo.info, tickerInfo.rate));
          }
          resolve(apiData.payload);
          onSuccess(apiData.payload);
        })
        .catch(apiError => {
          reject(apiError);
          if (!onError === false) {
            onError(apiError);
          }
        });
    });
  };
};

export const getTickersForText = (
  text,
  onSuccess,
  onError = () => null,
  needsParsing = true,
  forDate,
  portfolioCurrency
) => {
  return (dispatch, getState) => {
    const currencyInputVal = needsParsing
      ? extractValueAndCurrencyInputFromInput(getState(), text).currencyInput
      : text;
    // sometimes symbols were being passed into the API, so converting everything to shortName and removing whitespace
    const parseSymbol = getTickerUsingSymbolSelector(getState(), currencyInputVal.replace(/ /g, "")).shortName;
    const currencyInput = parseSymbol === "UNKNOWN" ? currencyInputVal : parseSymbol;
    if (needsParsing && /\s/.test(currencyInput.trim())) {
      Promise.resolve().then(() => onError(new ApiError({ errorCode: apiErrorCodes.INVALID_INPUT })));
      return;
    }
    const userCountryCode = userCountryCodeSelector(getState());
    var dateString;
    if (forDate) {
      dateString = getDateStringForExchangeRateDate(forDate);

      if (dateString === getDateStringForExchangeRateDate(toUserTimezoneDate())) {
        dateString = undefined;
        forDate = undefined;
      }
    }

    // Get ticker and exchange rate for portfolio currency in USD if
    // the portfolio curency is not USD and a date is specified for the ticker
    const emptyPromise = new Promise((resolve, reject) => {
      resolve(null);
    });
    const getPortfolioTickerForDate = () => {
      return portfolioCurrency === "USD" || !forDate === true
        ? emptyPromise
        : ApiClient.getTickersForText(getUuid(), portfolioCurrency, userCountryCode, dateString);
    };

    Promise.all([
      ApiClient.getTickersForText(getUuid(), currencyInput, userCountryCode, dateString),
      getPortfolioTickerForDate()
    ])
      .then(([textTickerData, portfolioCurrencyTickerData]) => {
        if (!portfolioCurrencyTickerData === false) {
          for (const tickerEntry of portfolioCurrencyTickerData.payload) {
            tickerEntry.info = expandTickerKeys(tickerEntry.info);

            dispatch(addTickerInfoAction(tickerEntry.info, tickerEntry.rate, forDate));
          }
        }

        for (const tickerEntry of textTickerData.payload) {
          tickerEntry.info = expandTickerKeys(tickerEntry.info);
        }
        onSuccess(textTickerData.payload);
      })
      .catch(apiError => {
        onError(apiError);
      });
  };
};

export const getTickerDescription = ticker => {
  if (!ticker === true) {
    return null;
  }
  if (
    ticker.type === tickerTypes.STOCK ||
    ticker.type === tickerTypes.FUND ||
    ticker.type === tickerTypes.BOND ||
    ticker.type === tickerTypes.DERIVATIVE ||
    ticker.type === tickerTypes.INDEX
  ) {
    const marketName = exchangeCodeToNameMap[ticker.market];
    if (!marketName === false) {
      return `${ticker.name} • ${marketName}`;
    }
  }
  return ticker.name;
};

export const getSanitizedExchangeRate = (rate, isCrypto = false) => {
  rate = parseFloat(rate);
  if (isCrypto === true) {
    return Math.kuberaRoundToDecimalPlaces(rate, 4);
  }
  if (rate < 0.0001) return rate;

  if (rate < 0.01) {
    return Math.kuberaRoundToDecimalPlaces(rate, 4);
  }
  if (rate < 1000) {
    return Math.kuberaRoundToDecimalPlaces(rate, 2);
  }
  return Math.kuberaFloor(rate);
};

export const formatSanitizedRate = (state, rate, currency, isCrypto = false) => {
  if (isCrypto === true) {
    return formatNumberWithKuberaNumberFormatSettingsSelector(state, rate, {
      maximumFractionDigits: 4,
      minimumFractionDigits: 4
    });
  }
  if (rate < 0.0001) return rate.toPrecision(2);
  if (rate < 0.01) return rate.toFixed(4);
  if (rate < 1000) return rate.toFixed(2);
  return shortFormatNumberWithCurrency(state, rate, currency, true);
};

export const searchTickersForText = (
  searchText,
  tickerType,
  allowUnusedTickers = false,
  fetchTickersWithoutRate = false,
  onSuccess
) => {
  return (dispatch, getState) => {
    const searchQuery = searchText.trim().toLowerCase();
    const data = { searchText: searchText, results: [] };

    ApiClient.searchTickerInfo(getUuid(), searchQuery, tickerType)
      .then(apiData => {
        for (const tickerEntry of apiData.payload) {
          const ticker = expandTickerKeys(tickerEntry);
          const quantity = portfolioQuantityForTicker(getState(), undefined, undefined, ticker.id);
          if (
            (fetchTickersWithoutRate === true || tickerEntry.rate !== undefined) &&
            (allowUnusedTickers === true || quantity > 0)
          ) {
            data.results.push(ticker);
            dispatch(addTickerInfoAction(ticker, tickerEntry.rate));
          }
        }
        onSuccess(data);
      })
      .catch(apiError => {
        onSuccess(data);
      });
  };
};

export const updateSupportedIndexes = indexes => dispatch => {
  dispatch(updateSupportedIndexesAction(indexes));
  ApiClient.setUserPreference(getUuid(), "popularMarket", JSON.stringify(indexes.map(item => item.id)))
    .then(() => {
      dispatch(refreshTickerData());
    })
    .catch(() => {});
};
